import React from 'react';
import { ExampleLinkWorkflow } from '../../../api/cafAutomation';
import { CafCorpusEntry } from '../../../api/cafCorpus';
import { STATE_LABEL, STATE_TONE } from '../automation/automationCopy';
import { formatAgo, formatCount } from '../format';
import { KebabMenu, MenuItem } from '../KebabMenu';
import { ActionPill } from '../LabelForm';
import { senderEmail } from '../lenders';
import { LenderOf, needsFinalResponseSetting } from './teachFilter';
import { NEEDS_FINAL_RESPONSE_SETTING } from '../copy';
import { useFinalResponseSupported } from '../finalResponseSupport';
import { WhoCell } from './WhoCell';

export type ExampleAction = 'edit' | 'history' | 'group' | 'rerun' | 'off' | 'on';

interface Props {
  rows: CafCorpusEntry[];
  compact?: boolean;
  lenderOf: LenderOf;
  canSwitchOn: boolean;
  flashId?: string | null;
  /** Overrides the table's accessible name (a lender card needs its own, e.g. "Approved examples — <Lender>"). */
  ariaLabel?: string;
  onOpen: (entry: CafCorpusEntry) => void;
  onAction: (action: ExampleAction, entry: CafCorpusEntry) => void;
  empty: React.ReactNode;
  workflowOf?: (entry: CafCorpusEntry) => ExampleLinkWorkflow | null;
  onOpenWorkflow?: (workflowId: string) => void;
}

/** "412 emails" / "1 email" (a one-email example with no stamped size still counts as 1) / "Open" when
 *  neither is known -- never a bare "Open" for a one-email example, which we can always derive. */
const coversLabel = (entry: CafCorpusEntry): string => {
  const size = entry.group_size ?? (entry.cluster_id == null ? 1 : null);
  return size != null ? `${formatCount(size)} ${size === 1 ? 'email' : 'emails'}` : 'Open';
};

const menuFor = (
  entry: CafCorpusEntry, canSwitchOn: boolean, act: (action: ExampleAction) => void, openWorkflow: (() => void) | null,
): MenuItem[] => {
  const history: MenuItem = { key: 'history', label: 'History', icon: 'clock', onSelect: () => act('history') };
  const group: MenuItem = { key: 'group', label: 'Emails in this group', icon: 'list', onSelect: () => act('group') };
  const workflow: MenuItem[] = openWorkflow ? [{ key: 'workflow', label: 'Open its workflow', icon: 'external', onSelect: openWorkflow }] : [];
  if (entry.active === false) {
    return [history, group, ...workflow, ...(canSwitchOn
      ? [{ key: 'on', label: 'Switch back on', icon: 'power', separatorBefore: true, onSelect: () => act('on') } as MenuItem]
      : [])];
  }
  return [
    { key: 'edit', label: 'Edit', icon: 'pencil', onSelect: () => act('edit') },
    history,
    group,
    ...workflow,
    { key: 'rerun', label: 'Re-run decisions', icon: 'refresh', onSelect: () => act('rerun') },
    { key: 'off', label: 'Switch off', icon: 'power', danger: true, separatorBefore: true, onSelect: () => act('off') },
  ];
};

const WorkflowCell: React.FC<{ workflow: ExampleLinkWorkflow | null; onOpen?: (workflowId: string) => void }> = ({ workflow, onOpen }) => {
  if (!workflow) return <span className="cur-muted">—</span>;
  return (
    <div className="cur-who">
      {onOpen ? (
        <button type="button" className="cur-linkish cur-ellip"
          onClick={(event) => { event.stopPropagation(); onOpen(workflow.workflow_id); }}>{workflow.name}</button>
      ) : <span className="cur-ellip">{workflow.name}</span>}
      <span><span className={`cur-pill cur-pill--${STATE_TONE[workflow.state]}`}>{STATE_LABEL[workflow.state]}</span></span>
    </div>
  );
};

export const ExamplesTable: React.FC<Props> = ({
  rows, compact = false, lenderOf, canSwitchOn, flashId, ariaLabel, onOpen, onAction, empty, workflowOf, onOpenWorkflow,
}) => {
  // Final fix wave C2: no stored example has the setting on an older backend, so no pill there.
  const askSetting = useFinalResponseSupported();
  if (!rows.length) return <div className="cur-empty">{empty}</div>;
  return (
    <div className="cur-table-wrap">
      <table className="cur-tbl" aria-label={ariaLabel ?? 'Approved examples'}>
        <colgroup>
          <col style={{ width: compact ? '28%' : '23%' }} /><col style={{ width: 252 }} />
          {workflowOf && <col style={{ width: 200 }} />}<col /><col style={{ width: 100 }} />
          {!compact && <col style={{ width: 124 }} />}<col style={{ width: 48 }} />
        </colgroup>
        <thead>
          <tr>
            <th>{compact ? 'Sender' : 'Lender / sender'}</th><th>What happens</th>{workflowOf && <th>Workflow</th>}<th>Why</th><th>Covers</th>
            {!compact && <th>Last changed</th>}<th><span className="cur-sr">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((entry) => {
            const lender = lenderOf(entry.sender);
            const who = lender ?? senderEmail(entry.sender);
            const when = entry.amended_at || entry.labelled_at;
            const changedBy = entry.amended_by || entry.labelled_by;
            const covers = coversLabel(entry);
            const workflow = workflowOf ? workflowOf(entry) : null;
            const openWorkflow = workflow && onOpenWorkflow ? () => onOpenWorkflow(workflow.workflow_id) : null;
            return (
              <tr key={entry.id} id={`cur-example-${entry.id}`} tabIndex={0}
                className={`cur-row-click${entry.active === false ? ' cur-is-off' : ''}${flashId === entry.id ? ' cur-just-added' : ''}`}
                onClick={() => onOpen(entry)}
                onKeyDown={(event) => {
                  // Enter/Space bubble up from a focusable descendant -- the kebab button, and (via
                  // React's portal event bubbling) its menu items -- so only act when the row itself
                  // is the target, never a click already handled by that descendant.
                  if (event.target !== event.currentTarget) return;
                  if (event.key === 'Enter') onOpen(entry);
                  else if (event.key === ' ') { event.preventDefault(); onOpen(entry); }
                }}>
                <td>
                  <WhoCell sender={entry.sender} lender={lender} compact={compact}
                    extra={entry.cluster_id == null ? <span className="cur-tag-hand">From one email</span> : undefined} />
                </td>
                <td><ActionPill action={entry.correct_action} />{entry.active === false && <> <span className="cur-pill cur-pill--grey">Off</span></>}
                  {entry.active && entry.approved_active === false && <> <span className="cur-pill cur-pill--grey">Needs approval review</span></>}
                  {askSetting && entry.active && needsFinalResponseSetting(entry) && <> <span className="cur-pill cur-pill--amber">{NEEDS_FINAL_RESPONSE_SETTING}</span></>}
                </td>
                {workflowOf && <td><WorkflowCell workflow={workflow} onOpen={onOpenWorkflow} /></td>}
                <td><div className="cur-clamp2" title={entry.note || ''}>{entry.note}</div></td>
                <td>
                  <button type="button" className="cur-linkish" onClick={(event) => { event.stopPropagation(); onOpen(entry); }}
                    aria-label={covers === 'Open' ? `Open the emails for ${who}` : `Open the ${covers} for ${who}`}>
                    {covers}
                  </button>
                </td>
                {!compact && (
                  <td>
                    <div className="cur-nowrap">{when ? formatAgo(when) : '—'}</div>
                    <div className="cur-muted cur-small cur-ellip" title={changedBy || ''}>{changedBy}</div>
                  </td>
                )}
                <td className="cur-right" onClick={(event) => event.stopPropagation()}>
                  <KebabMenu label={`Actions for ${who} example`}
                    items={menuFor(entry, canSwitchOn, (action) => onAction(action, entry), openWorkflow)} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
